import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import {
  ApiResponse,
  AuthResponse,
  AuthTokens,
  LoginRequest,
  PaginationInfo,
  Protocol,
  RegisterRequest,
  SearchParams,
  SearchResult,
  SurgicalProcedureCoverage,
  User,
} from '../types/index';

class ApiService {
  private api: AxiosInstance;
  private baseURL: string;

  constructor() {
    this.baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api/v1';

    this.api = axios.create({
      baseURL: this.baseURL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      config => {
        const tokens = this.getTokens();
        if (tokens?.accessToken) {
          config.headers.Authorization = `Bearer ${tokens.accessToken}`;
        }
        return config;
      },
      error => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle token refresh
    this.api.interceptors.response.use(
      response => response,
      async error => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          // Don't redirect if this is the /auth/me endpoint (used for initialization)
          const isAuthCheck = originalRequest.url?.includes('/auth/me');

          try {
            const tokens = this.getTokens();
            if (tokens?.refreshToken) {
              const newTokens = await this.refreshToken(tokens.refreshToken);
              this.setTokens(newTokens);

              // Retry the original request with new token
              originalRequest.headers.Authorization = `Bearer ${newTokens.accessToken}`;
              return await this.api(originalRequest);
            }
          } catch (refreshError) {
            // Refresh failed
            this.clearTokens();
            // Only redirect if not checking auth status (let AuthContext handle it)
            if (!isAuthCheck) {
              window.location.href = '/login';
            }
          }
        }

        return Promise.reject(error);
      }
    );
  }

  // Token management
  private getTokens(): AuthTokens | null {
    const tokens = localStorage.getItem('auth_tokens');
    return tokens ? JSON.parse(tokens) : null;
  }

  private setTokens(tokens: AuthTokens): void {
    localStorage.setItem('auth_tokens', JSON.stringify(tokens));
  }

  private clearTokens(): void {
    localStorage.removeItem('auth_tokens');
  }

  // Authentication methods
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    const response = await this.api.post<
      ApiResponse<{
        user: User;
        token: string;
      }>
    >('/auth/login', credentials);
    if (response.data.success && response.data.data) {
      // Convert backend response format to frontend format
      const backendData = response.data.data;
      const authResponse: AuthResponse = {
        user: {
          ...backendData.user,
          _id: (backendData.user as any).id, // Convert id to _id
        },
        tokens: {
          accessToken: backendData.token,
          refreshToken: backendData.token, // Simple backend doesn't have refresh tokens
        },
      };
      this.setTokens(authResponse.tokens);
      return authResponse;
    }
    throw new Error(response.data.message || 'Login failed');
  }

  async register(userData: RegisterRequest): Promise<AuthResponse> {
    const response = await this.api.post<
      ApiResponse<{
        user: User;
        token: string;
      }>
    >('/auth/register', userData);
    if (response.data.success && response.data.data) {
      // Convert backend response format to frontend format
      const backendData = response.data.data;
      const authResponse: AuthResponse = {
        user: {
          ...backendData.user,
          _id: (backendData.user as any).id, // Convert id to _id
        },
        tokens: {
          accessToken: backendData.token,
          refreshToken: backendData.token, // Simple backend doesn't have refresh tokens
        },
      };
      this.setTokens(authResponse.tokens);
      return authResponse;
    }
    throw new Error(response.data.message || 'Registration failed');
  }

  async refreshToken(refreshToken: string): Promise<AuthTokens> {
    const response = await this.api.post<ApiResponse<AuthTokens>>('/auth/refresh', {
      refreshToken,
    });
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error('Token refresh failed');
  }

  async logout(): Promise<void> {
    try {
      await this.api.post('/auth/logout');
    } finally {
      this.clearTokens();
    }
  }

  async getCurrentUser(): Promise<User> {
    const response = await this.api.get<ApiResponse<User>>('/auth/me');
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error('Failed to get current user');
  }

  // Protocol methods
  async getProtocols(params?: {
    page?: number;
    limit?: number;
    category?: string;
    status?: string;
    search?: string;
    sortBy?: string;
    sortOrder?: string;
  }): Promise<{ protocols: Protocol[]; pagination: PaginationInfo }> {
    const response = await this.api.get<{
      success: boolean;
      data: Protocol[];
      pagination: PaginationInfo;
    }>('/protocols', { params });
    if (response.data.success && response.data.data) {
      const result = {
        protocols: response.data.data,
        pagination: response.data.pagination || {
          page: 1,
          limit: 20,
          total: 0,
          pages: 0,
          hasNext: false,
          hasPrev: false,
        },
      };
      // Debug log (remove in production)
      console.log('API getProtocols response:', {
        protocolsCount: result.protocols.length,
        pagination: result.pagination,
      });
      return result;
    }
    throw new Error('Failed to fetch protocols');
  }

  async getProtocol(id: string): Promise<Protocol> {
    try {
      const response = await this.api.get<ApiResponse<Protocol>>(`/protocols/${id}`);
      console.log('API Response:', response.data);

      if (response.data.success && response.data.data) {
        return response.data.data;
      }

      throw new Error(response.data.message || 'Failed to fetch protocol');
    } catch (error: any) {
      console.error('getProtocol error:', error);
      if (error.response) {
        console.error('Error response:', error.response.data);
        throw new Error(error.response.data?.message || 'Failed to fetch protocol');
      }
      throw error;
    }
  }

  async searchProtocols(params: SearchParams): Promise<SearchResult> {
    // Convert SearchParams to query parameters for the backend search endpoint
    const queryParams: Record<string, string | number> = {
      page: params.page || 1,
      limit: params.limit || 10,
    };

    if (params.query) {
      queryParams.query = params.query;
    }

    if (params.filters?.category) {
      queryParams.category = params.filters.category;
    }

    if (params.filters?.status) {
      queryParams.status = params.filters.status;
    }

    if (params.sortBy) {
      queryParams.sortBy = params.sortBy;
    }

    if (params.sortOrder) {
      queryParams.sortOrder = params.sortOrder;
    }

    const response = await this.api.get<{
      success: boolean;
      data: Protocol[];
      pagination: PaginationInfo;
    }>('/protocols/search', {
      params: queryParams,
    });
    if (response.data.success && response.data.data) {
      return {
        protocols: response.data.data,
        pagination: response.data.pagination || {
          total: response.data.data.length,
          page: 1,
          pages: 1,
          limit: response.data.data.length,
          hasNext: false,
          hasPrev: false,
        },
        totalResults: response.data.pagination?.total || response.data.data.length,
      };
    }
    throw new Error('Search failed');
  }

  async createProtocol(protocol: Partial<Protocol>): Promise<Protocol> {
    const response = await this.api.post<ApiResponse<Protocol>>('/protocols', protocol);
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Failed to create protocol');
  }

  async updateProtocol(id: string, protocol: Partial<Protocol>): Promise<Protocol> {
    const response = await this.api.put<ApiResponse<Protocol>>(`/protocols/${id}`, protocol);
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Failed to update protocol');
  }

  async deleteProtocol(id: string): Promise<void> {
    const response = await this.api.delete<ApiResponse<void>>(`/protocols/${id}`);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete protocol');
    }
  }

  async incrementViewCount(id: string): Promise<void> {
    await this.api.post(`/protocols/${id}/view`);
  }

  async incrementUsageCount(id: string): Promise<void> {
    await this.api.post(`/protocols/${id}/use`);
  }

  // User methods
  async getUsers(params?: {
    page?: number;
    limit?: number;
    role?: string;
    search?: string;
  }): Promise<{ users: User[]; pagination: PaginationInfo }> {
    const response = await this.api.get<ApiResponse<User[]>>('/users', { params });
    if (response.data.success && response.data.data) {
      return {
        users: response.data.data,
        pagination: response.data.pagination || {
          page: 1,
          limit: 20,
          total: 0,
          pages: 0,
          hasNext: false,
          hasPrev: false,
        },
      };
    }
    throw new Error(response.data.message || 'Failed to fetch users');
  }

  async getUser(id: string): Promise<User> {
    const response = await this.api.get<ApiResponse<User>>(`/users/${id}`);
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Failed to fetch user');
  }

  async updateUser(id: string, user: Partial<User>): Promise<User> {
    const response = await this.api.put<ApiResponse<User>>(`/users/${id}`, user);
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Failed to update user');
  }

  async deleteUser(id: string): Promise<void> {
    const response = await this.api.delete<ApiResponse<void>>(`/users/${id}`);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete user');
    }
  }

  // Surgical Procedure Coverage methods
  async getSurgicalProcedures(params?: {
    page?: number;
    limit?: number;
    search?: string;
    isActive?: boolean;
    sortBy?: string;
    sortOrder?: string;
  }): Promise<{ procedures: SurgicalProcedureCoverage[]; pagination: PaginationInfo }> {
    try {
      const response = await this.api.get<{
        success: boolean;
        data: SurgicalProcedureCoverage[];
        pagination: PaginationInfo;
      }>('/surgical-procedures', { params });
      if (response.data.success && response.data.data) {
        return {
          procedures: response.data.data,
          pagination: response.data.pagination || {
            page: 1,
            limit: 20,
            total: 0,
            pages: 0,
            hasNext: false,
            hasPrev: false,
          },
        };
      }
      throw new Error('Failed to fetch surgical procedures');
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error(
          'El endpoint de procedimientos quirúrgicos no está disponible. Por favor, verifica que el backend esté configurado correctamente.'
        );
      }
      throw new Error(
        error.response?.data?.message ||
          error.message ||
          'Error al cargar procedimientos quirúrgicos'
      );
    }
  }

  async getSurgicalProcedure(id: string): Promise<SurgicalProcedureCoverage> {
    const response = await this.api.get<ApiResponse<SurgicalProcedureCoverage>>(
      `/surgical-procedures/${id}`
    );
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Failed to fetch surgical procedure');
  }

  async createSurgicalProcedure(
    procedure: Partial<SurgicalProcedureCoverage>
  ): Promise<SurgicalProcedureCoverage> {
    const response = await this.api.post<ApiResponse<SurgicalProcedureCoverage>>(
      '/surgical-procedures',
      procedure
    );
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Failed to create surgical procedure');
  }

  async updateSurgicalProcedure(
    id: string,
    procedure: Partial<SurgicalProcedureCoverage>
  ): Promise<SurgicalProcedureCoverage> {
    const response = await this.api.put<ApiResponse<SurgicalProcedureCoverage>>(
      `/surgical-procedures/${id}`,
      procedure
    );
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Failed to update surgical procedure');
  }

  async deleteSurgicalProcedure(id: string): Promise<void> {
    const response = await this.api.delete<ApiResponse<void>>(`/surgical-procedures/${id}`);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete surgical procedure');
    }
  }

  // Health check
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    const response =
      await this.api.get<ApiResponse<{ status: string; timestamp: string }>>('/health');
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error('Health check failed');
  }

  // Generic request method
  async request<T = unknown>(config: AxiosRequestConfig): Promise<T> {
    const response = await this.api.request<ApiResponse<T>>(config);
    if (response.data.success && response.data.data !== undefined) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Request failed');
  }
}

export const apiService = new ApiService();
export default apiService;
